/**
 * Story progression.
 *
 * Chapters unlock strictly in order: you can open chapter N once chapter N-1
 * is complete. That's the whole point of a storyline — reading Act V before
 * Act I would give you the events without the arc.
 *
 * Progress is stored as `state.story[chapterId] = { done, correct }`:
 *   done     the chapter was read through and its question answered
 *   correct  whether that answer was right — only affects XP and the badge,
 *            never whether the next chapter opens. Getting a question wrong
 *            shouldn't wall someone out of the rest of the Bible.
 */

import { ACTS, CHAPTERS, STORY_TOTAL } from '../data/story.js';
import { FREE_LIMITS } from './tiers.js';

export { ACTS, CHAPTERS, STORY_TOTAL };

/** How many chapters a free player may open before the paywall. */
export const FREE_CHAPTERS = FREE_LIMITS.storyChapters;

export function isChapterDone(story, chapterId) {
  return !!story?.[chapterId]?.done;
}

/**
 * 0–3 stars, each one a distinct thing the player actually did:
 *
 *   ★    read the chapter through and answered its question
 *   ★★   answered it correctly
 *   ★★★  answered it correctly the first time
 *
 * The third is the only one that can't be won back by replaying, which is what
 * gives a first read any weight.
 */
export function chapterStars(story, chapterId) {
  const record = story?.[chapterId];
  if (!record?.done) return 0;
  if (!record.correct) return 1;
  return record.firstTry ? 3 : 2;
}

export const MAX_STARS = 3;

/** Stars earned across the whole story, for the header rollup. */
export function totalStars(story) {
  return CHAPTERS.reduce((n, c) => n + chapterStars(story, c.id), 0);
}

/**
 * Locked because of where the player is in the story, not what they've paid.
 * The first chapter is always open.
 */
export function isChapterSequenceLocked(story, chapterIndex) {
  if (chapterIndex <= 0) return false;
  return !isChapterDone(story, CHAPTERS[chapterIndex - 1].id);
}

/** Locked behind premium — everything past the free allowance. */
export function isChapterPremiumLocked(chapterIndex, isPremium) {
  return !isPremium && chapterIndex >= FREE_CHAPTERS;
}

/**
 * @returns {'done'|'current'|'locked'|'premium'} the single state a chapter
 *   badge should render. Premium wins over sequence, so a free player sees
 *   "unlock to continue" rather than a padlock they can't ever open.
 */
export function chapterStatus(story, chapterIndex, isPremium) {
  const chapter = CHAPTERS[chapterIndex];
  if (isChapterDone(story, chapter.id)) return 'done';
  if (isChapterPremiumLocked(chapterIndex, isPremium)) return 'premium';
  if (isChapterSequenceLocked(story, chapterIndex)) return 'locked';
  return 'current';
}

/** The chapter the player should be pointed at next, or null once finished. */
export function nextChapter(story) {
  return CHAPTERS.find((c) => !isChapterDone(story, c.id)) ?? null;
}

export function completedCount(story) {
  return CHAPTERS.reduce((n, c) => n + (isChapterDone(story, c.id) ? 1 : 0), 0);
}

/** Per-act rollup for the map: how far through each act the player is. */
export function actProgress(story) {
  let index = 0;
  return ACTS.map((act) => {
    const chapters = act.chapters.map((chapter) => {
      const flatIndex = index++;
      return {
        ...chapter,
        index: flatIndex,
        // `order` lives on the flattened CHAPTERS list, not on the raw act
        // data these are spread from, so it has to be restated here.
        order: flatIndex + 1,
        done: isChapterDone(story, chapter.id),
        stars: chapterStars(story, chapter.id),
      };
    });
    const done = chapters.filter((c) => c.done).length;
    const stars = chapters.reduce((n, c) => n + c.stars, 0);
    return {
      ...act,
      chapters,
      done,
      stars,
      starsTotal: chapters.length * MAX_STARS,
      total: chapters.length,
      complete: done === chapters.length,
      // An act is "open" once any of its chapters has been reached.
      started: done > 0 || chapters.some((c) => !isChapterSequenceLocked(story, c.index)),
    };
  });
}

/** Overall 0–1 progress through the story, for the rail on the map. */
export function storyProgress(story) {
  return STORY_TOTAL ? completedCount(story) / STORY_TOTAL : 0;
}
